import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicsByCategory } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const [searchParams] = useSearchParams();
  const activeCategory = searchParams.get('cat') || '全部';
  const [selectedCategory, setSelectedCategory] = useState(activeCategory);
  const displayedComics = getComicsByCategory(selectedCategory);

  useEffect(() => {
    setSelectedCategory(activeCategory);
  }, [activeCategory]);

  const featuredComic = displayedComics.length > 0 ? displayedComics[0] : null;
  const restComics = displayedComics.slice(1);

  const categoryLabel = categories.find((c) => c.key === selectedCategory)?.label || '全部漫画';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '漫画分类', href: '/category' },
    { label: categoryLabel },
  ];

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryLabel} - 叮当漫画网`,
    url: `http://kjbooks.cn/category?cat=${selectedCategory}`,
    description: `叮当漫画网${categoryLabel}分类，收录优质${categoryLabel}漫画作品，下拉式免费阅读。`,
    isPartOf: { '@id': 'http://kjbooks.cn/#website' },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="text-center mb-10">
                <span className="inline-block px-3 py-1 border border-background-300/50 text-foreground-400 text-xs rounded-full whitespace-nowrap">
                  分类浏览
                </span>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-100 mt-4 leading-relaxed">
                  {categoryLabel}
                </h1>
                <p className="text-foreground-500 text-sm mt-3 max-w-lg mx-auto">
                  发现最精彩的{categoryLabel}漫画作品，下拉式阅读，沉浸式体验
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-8 justify-center">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={`/category?cat=${cat.key}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      selectedCategory === cat.key
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200/70'
                    }`}
                  >
                    {cat.label}
                  </a>
                ))}
              </div>

              {displayedComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {featuredComic && restComics.length >= 1 && (
                    <div className="col-span-2 row-span-2">
                      <ComicCard
                        id={featuredComic.id}
                        title={featuredComic.title}
                        cover={featuredComic.cover}
                        author={featuredComic.author}
                        status={featuredComic.status}
                        category={featuredComic.category}
                        rating={featuredComic.rating}
                        views={featuredComic.views}
                        tags={featuredComic.tags}
                        isHot={featuredComic.isHot}
                        isNew={featuredComic.isNew}
                        variant="featured"
                      />
                    </div>
                  )}
                  {restComics.map((comic) => (
                    <ComicCard
                      key={comic.id}
                      id={comic.id}
                      title={comic.title}
                      cover={comic.cover}
                      author={comic.author}
                      status={comic.status}
                      category={comic.category}
                      rating={comic.rating}
                      views={comic.views}
                      tags={comic.tags}
                      isHot={comic.isHot}
                      isNew={comic.isNew}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-emotion-sad-line text-foreground-500 text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无漫画作品</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}